<script>
import {defineComponent} from 'vue';
import {delayFrames} from '../../../utility';
import VTooltipWrapper from "../../common/v-tooltip-wrapper.vue";
import {RELOAD_IMAGE_TIMEOUT} from "./consts";

const IMG_CONTAINED_WIDTH = 200;
const IMG_CONTAINED_HEIGHT = 136;
const IMG_DESIRED_ASPECT_RATIO = IMG_CONTAINED_WIDTH / IMG_CONTAINED_HEIGHT;

const DEBUG = false;

export default defineComponent({
    name: 'thumbnailHoverZoom',
    components: {VTooltipWrapper},
    props: {
        entry: {
            /** @type Entry */
            type: Object,
            required: true,
            default: {}
        },
        editLink: String,
        showIcon: Boolean,
        isThumbnail: Boolean,
        isWidget: Boolean,
        isDnDElement: Boolean,
        src: String,
        cursorIcon: String,
    },
    data() {
        return {
            imgCompleted: false,
            showTooltip: false,
            scaleFactor: 1,
            scaleFactorHover: 1,
            timeoutCount: 0,
            firstLoad: true
        }
    },
    computed: {
        fallbackSource() {
            if (this.isThumbnail) return '/storage/thumb/fallback';
            return '/storage/preview/fallback';
        },
        source() {
            if (this.src) return this.src;
            if (this.entry.preview) return this.entry.preview;
            if (this.entry.last_thumb) return this.entry.last_thumb;

            return this.fallbackSource;
        },
        name() {
            // check showGlobalName for the widget overview
            if (this.entry.showGlobalName) {
                return '*** ' + this.entry.name;
            }
            if (this.entry.name) return this.entry.name;
            return this.entry.file_name;
        },
        translations() {
            return {
                added: Lang.langGet("Created"),
                timeOnDevice: Lang.langGet("Time on device"),
                modified: Lang.langGet("Modified"),
                validity: Lang.langGet("Validity"),
                file_name: Lang.langGet("Filename"),
                version: Lang.langGet("Version")
            }
        },
        tooltipHtml() {
            let data = this.entry.name ? this.entry.name + '<br/>' : this.entry.display_name ? this.entry.display_name + '<br/>' : '';

            // different naming convention for widgets
            if (this.isWidget) {
                data = this.entry.showGlobalName ? '*** ' + this.entry.name + '<br/>' : this.entry.name + '<br/>';
                data = data + this.translations.version + ': ' + this.entry.version + '<br/>';
            }

            if (this.entry.file_name && this.entry.file_name !== this.entry.name && this.entry.file_name !== this.entry.display_name) {
                data = data + this.translations.file_name + ': ' + this.entry.file_name;
                if (this.entry.file_extension) {
                    data = data + '.' + this.entry.file_extension + '<br/>';
                } else {
                    data = data + '<br/>';
                }
            }
            if (this.entry?.parameters?.hasOwnProperty('validity-text')) {
                data = data + this.translations.validity + ': ' + this.entry.parameters['validity-text'] + '<br/>';
            }

            data+= this.translations.added + ': ';
            if (this.entry.created_at_local) {
                data+= this.entry.created_at_local;
            } else {
                data+= this.entry.created_at;
            }

            if (this.entry.updated_at_local || this.entry.updated_at) {
                data+= '<br/>'+ this.translations.modified + ': ';
                if (this.entry.updated_at_local) {
                    data+= this.entry.updated_at_local;
                } else {
                    data+= this.entry.updated_at;
                }
            }

            if (this.entry.created_at !== '' && (this.entry.timeOnDevice)
                && this.entry.created_at !== this.entry.timeOnDevice ) {
                data+= '<br/>' + this.translations.timeOnDevice + ': ' + this.entry.timeOnDevice + '<br/>';
            }
            return data;
        },
        imgStyle() {
            const style = {
                '--scale-factor': this.scaleFactor,
                '--scale-factor-hover': this.scaleFactorHover,
                opacity: 0
            };

            if (this.imgCompleted) {
                style.opacity = 1;
                style['transition'] = 'transform 150ms ease-out, opacity 150ms ease-out';
            }

            return style;
        },
        isChildOfMediapool() {
            if (!this.editLink) return false;

            return this.editLink.includes('mediapool');
        },
        calculateValidityStatus() {
            if (this.entry?.parameters && this.entry?.parameters?.hasOwnProperty('validity-status')) {
                return this.entry.parameters['validity-status'];
            } else if (this.entry?.validity) {
                return this.entry.validity;
            }

            return '';
        },
        hasInfoClick() {
            return (this.$listeners.infoClick);
        },
    },
    async mounted() {
        await this.loadImage();
    },
    methods: {
        async loadImage() {
            if (DEBUG) console.log(`Loading preview ${this.name}, try number ${this.timeoutCount} with delay of ${RELOAD_IMAGE_TIMEOUT[this.timeoutCount]}ms`);
            this.timeoutCount++;

            const img = new Image();
            img.src = this.source;

            img.onload = () => {
                if (DEBUG) console.log(`Loading preview ${this.name} - Success!`);
                this.firstLoad = false;

                this.imgCompleted = true;
                setTimeout(this.scaleLoadedImage, 10);
            }
            img.onerror = () => {
                if (DEBUG) console.log(`Loading preview ${this.name} - Errored!`);
                this.firstLoad = false;

                if (this.timeoutCount < RELOAD_IMAGE_TIMEOUT.length) {
                    this.timeout = setTimeout(() => {
                        this.loadImage();
                    }, RELOAD_IMAGE_TIMEOUT[this.timeoutCount]);
                }
            }
        },
        async scaleLoadedImage() {
            const element = $(`#image-${this.entry.id}-preview`)[0];

            if (!element) {
                setTimeout(this.scaleLoadedImage, 100);
                return;
            }

            if (!element.naturalWidth) {
                setTimeout(this.scaleLoadedImage, 100);
                return;
            }

            const aspectRatio = element.naturalWidth / element.naturalHeight;

            // aspectRatio is above IMG_DESIRED_ASPECT_RATIO (default 2 at time of writing) for images which would have
            // margins on top/bottom when displayed in a box with aspect ratio of IMG_DESIRED_ASPECT_RATIO.
            // Calculate scale factors to fit image into constrained dimensions.
            // multiply with 1.0008 to prevent single pixel while lines above and below the image when rendered in different zoom levels.
            if (aspectRatio >= IMG_DESIRED_ASPECT_RATIO) {
                this.scaleFactor = IMG_CONTAINED_HEIGHT / element.naturalHeight * 1.0008;
                this.scaleFactorHover = IMG_CONTAINED_WIDTH / element.naturalWidth * 1.0008;
            } else {
                this.scaleFactor = IMG_CONTAINED_WIDTH / element.naturalWidth * 1.0008;
                this.scaleFactorHover = IMG_CONTAINED_HEIGHT / element.naturalHeight * 1.0008;
            }

            if (element.src.includes('img/Medienpool_') || element.src.includes('img/emptyWidget.png')) {
                this.scaleFactor = IMG_CONTAINED_HEIGHT / element.naturalHeight * 1.0008;
                this.scaleFactorHover = this.scaleFactor;
            }

            // Wait a bit longer than a frame at 60fps (no idea why, lower values don't work) to prevent the image zoom
            // transition from happening on image load.
            await delayFrames(2);
        }
    }
})

/**
 * @typedef {Object} Entry
 * @property {?number} id
 * @property {?string} name
 * @property {?string} file_name
 * @property {?string} last_thumb
 * @property {?Object} parameters
 */

</script>

<template>
    <div class="thumbnail-hoverzoom-container">
        <div :class="['thumbnail-hoverzoom', cursorIcon]">
            <div v-if="isChildOfMediapool || isDnDElement"
                 @click="$emit('thumbnailPreviewClick', entry?.id)"
                 class="thumbnail-hoverzoom-img-container"
                 tabindex="-1"
            >
                <img v-if="imgCompleted || firstLoad"
                     :id="`image-${this.entry.id}-preview`"
                     :alt="`Preview of ${name}`"
                     :src="source"
                     :style="imgStyle"
                />
                <img v-else
                    :id="`image-${this.entry.id}-preview`"
                    :alt="`Preview of ${name}`"
                    :src="fallbackSource"
                />
            </div>
            <a v-else
               :href="editLink"
               class="thumbnail-hoverzoom-img-container"
               tabindex="-1"
            >
                <img v-if="imgCompleted"
                     :id="`image-${this.entry.id}-preview`"
                     :alt="`Preview of ${name}`"
                     :src="source"
                     :style="imgStyle"
                />
                <img v-else
                     :id="`image-${this.entry.id}-preview`"
                     :alt="`Preview of ${name}`"
                     :src="fallbackSource"
                />
            </a>

            <div class="video-overlay overlay-fullsize" v-if="entry?.mime_type?.includes('video')" style="height: 136px;">
                <div class="wrap" @click.self="$emit('thumbnailPreviewClick', $event)">
                    <i class="fa-regular fa-play fa-3x text-primary" @click.self="$emit('thumbnailPreviewClick', $event)"></i>
                </div>
            </div>
        </div>
        <div
            :class="['d-flex align-center image-title-text py-1 px-2 bg-white', showIcon ? 'justify-center' : '', calculateValidityStatus, hasInfoClick ? 'cursor-pointer':'']"
            :id="`thumbnail-hoverzoom-info-${entry.id}`"
            @click="$emit('infoClick', entry?.id)">
            <div class="mr-1" @mouseover="showTooltip = true" @mouseleave="showTooltip = false" >
                <i class="fa-regular fa-circle-info"></i>
            </div>
            <div class="text-truncate">{{ name }}</div>
        </div>
        <v-tooltip-wrapper v-if="entry.created_at || entry.created_at_local" :activator="'#thumbnail-hoverzoom-info-' + entry.id">
            <div v-html="tooltipHtml"/>
        </v-tooltip-wrapper>
    </div>
</template>

<style lang="scss">
    :root {
        --scale-factor: 1;
        --scale-factor-hover: 1;
        --thumbnail-hover-zoom-height: 121px;
    }

    .thumbnail-hoverzoom-container {
        outline: white solid 1px;
        border-radius: 4px !important;

        .bg-white {
            background-color: white !important;
        }

        .thumbnail-hoverzoom {
            cursor: pointer;

            .thumbnail-hoverzoom-img-container {
                width: 100%;
                height: var(--thumbnail-hover-zoom-height);
                overflow: clip;
                display: flex;
                justify-content: center;
                align-items: center;
                border-top-left-radius: 4px !important;
                border-top-right-radius: 4px !important;

                img {
                    line-height: var(--thumbnail-hover-zoom-height);

                    transform: scale(var(--scale-factor));

                    /* 'color' of img tag will not affect the image, but it will change the color of the 'alt' text when image is unavailable */
                    color: white;
                }
            }
        }
    }
</style>
